import placement from './placement-en'

/**
 * The drawing standard as the check enforces it (planning plan DS): the checklist on New submission and in the
 * Resubmit dialog, the what-to-fix lines under a finding, and the next step after a failed check. No layer name
 * appears here: names come from the pack's profile (PackDto.profiles[].layers); these sentences are keyed by a layer's
 * role. ms and zh use the same placeholders (planning-console-keys.spec.ts).
 */
export default {
  drawingStandard: {
    title: 'Drawing standard: {{profile}}',
    intro:
      'The check reads the drawing strictly against this standard. Layer names must match exactly (letter case is ignored); no other name is accepted.',
    layersTitle: 'Layers',
    required: 'Required',
    optional: 'Optional',
    roles: {
      site_boundary: 'Exactly one closed polyline or hatch: the site boundary. Nothing else on this layer.',
      landuse:
        'Each plot as its own closed polyline or hatch, inside the site. Open lines inside the site fail the check.',
      labels: 'Plain single-line text (TEXT) inside each plot, reading exactly one of the land-use class names below.',
      context: 'Supporting detail where relevant (contours, drainage, existing buildings). Not checked.',
      ignored: 'Anything else the plan needs. Read and ignored.',
    },
    hintsOnly:
      'When a layer name is close to a standard one, the check report names the right layer as a hint only; the drawing still fails until the layer is renamed.',
    rulesTitle: 'Rules for the whole drawing',
    rules: {
      modelSpace: 'Draw everything in model space. Layouts (paper space) are not read.',
      units: 'Draw in metres, at the true coordinates of the coordinate system you choose ({{crs}}).',
      noBlocks: 'No blocks on the required layers: explode them first. Block contents are not read.',
      closure: 'The site boundary must close: a gap wider than {{tolerance}} m fails.',
      area: 'The declared area (hectares) must match the site boundary within {{pct}}%.',
      format: 'Save as a DWG from AutoCAD 2000 to 2018, or as a DXF.',
    },
    classesTitle: 'Land-use class names for label text',
    classesHint:
      'Each label must read one of these names in full. Letter case is ignored; no other text classifies a plot.',
    template: 'Download a conforming template (DXF)',
    templateHint:
      'Its example site measures {{hectares}} ha and sits at a placeholder position: draw your own site at its true coordinates.',
    templateHintPlaced:
      'Its example site measures {{hectares}} ha and opens where you clicked: draw your own site at its true coordinates.',
    pick: {
      open: 'Open the template at my site',
      hint: 'Optional. Click your site on a map and the template opens there, in the coordinate system you chose. It is only a starting point: draw your own site at its true coordinates.',
      unavailable: 'This install cannot place the template on a map here, so it opens at its placeholder position.',
      map: 'Map to choose your site',
      click: 'Click your site on the map.',
      placed: 'The template opens at {{x}}, {{y}} in EPSG:{{srid}}.',
      failed: 'That place could not be read in EPSG:{{srid}}. Click inside the area this coordinate system covers.',
      clear: 'Clear',
    },
  },
  findingHelp: {
    layerAbsent: 'No layer in the drawing is named {{layer}}. Layer names must match exactly; letter case is ignored.',
    layerUnusable: 'Layer {{layer}} exists but holds nothing the check can use in model space.',
    layerExact: 'Layer names must match exactly; letter case is ignored.',
    nearMiss: 'Layer "{{near}}" looks like {{layer}}, but it is not read: rename it to exactly {{layer}}.',
    holds: {
      site_boundary: '{{layer}} must hold exactly one closed polyline or hatch: the site boundary.',
      landuse: '{{layer}} must hold each plot as a closed polyline or hatch.',
      labels: '{{layer}} must hold plain text naming a land-use class inside each plot.',
      context: '{{layer}} may hold any supporting detail.',
      ignored: '{{layer}} may hold anything; it is not read.',
    },
    unknownMore: '…and {{more}} more not listed here.',
    unknownExpected: "The standard's layers are: {{expected}}.",
    paperSpace: 'Only model space is read; anything in a layout (paper space) is ignored.',
    oneLayer:
      'Everything in model space is on one layer, "{{layer}}". Put the site boundary, the plots and the labels each on its own standard layer.',
    noClosedShapes:
      'Its lines are open. Draw each plot as a closed polyline or a hatch; open lines do not count as plots.',
    plotsDropped:
      'None of its closed shapes gives a plot inside the site: they lie outside it, only touch its edge, or could not be repaired. Draw each plot as its own closed shape inside the site.',
    openLines:
      'Draw every plot on {{layer}} as a closed polyline or a hatch: close each open line into the outline of its plot, or delete it.',
    crsFitsOne:
      'Your drawing looks like it is in {{crs}}. Check it in your CAD software, change the declared CRS and resubmit.',
    crsFitsSeveral:
      'Your drawing would fall inside the jurisdiction in any of these: {{list}}. Check in your CAD software which one it is in, change the declared CRS and resubmit.',
    crsFitsNone:
      'None of the coordinate systems on offer puts the site inside the jurisdiction. Check that the drawing is at its true coordinates, not at local ones near 0,0, and resubmit.',
    crsCheck:
      'Check in your CAD software which coordinate system the drawing is in and that it is at its true coordinates. Declare that system and resubmit.',
    landedAt:
      'Read in EPSG:{{srid}}, the centre of the site is at {{x}}, {{y}}: longitude {{longitude}}, latitude {{latitude}}.',
    unitsCheck:
      'The drawing is read in metres. Check its units in your CAD software, scale it to metres if it is in another unit, and resubmit.',
    areaCompare: 'Compare the site boundary with the area on the form, correct whichever is wrong and resubmit.',
    areaCentimetres:
      'The site measures about 10,000 times the declared area. Either figure may be in the wrong unit: the form asks for hectares, and a drawing in centimetres also gives this. Check the declared figure first. Scale the drawing to metres only if that figure is right, then resubmit.',
    areaMillimetres:
      'The site measures about 1,000,000 times the declared area. Either figure may be in the wrong unit: the form asks for hectares, and a drawing in millimetres also gives this. Check the declared figure first. Scale the drawing to metres only if that figure is right, then resubmit.',
    areaFeet:
      'The site measures about 10.8 times the declared area. Either figure may be in the wrong unit: the form asks for hectares, and a drawing in feet also gives this. Check the declared figure first. Scale the drawing to metres only if that figure is right, then resubmit.',
    areaSquareMetres:
      'The declared figure looks like square metres: it is about 10,000 times the area the site measures. The form asks for hectares, so check it and enter it in hectares (a hectare is 10,000 square metres), then resubmit.',
    datumCheck:
      'The check cannot tell these grids apart from the drawing alone. Ask whoever prepared the drawing which datum it uses, and declare that coordinate system.',
    noJurisdiction:
      'There is nothing to fix in the drawing for this. Without a boundary a wrong coordinate system is not caught here, so check the one you declared in your CAD software.',
  },
  nextStep: {
    title: 'Next step: upload a corrected drawing',
    body: 'The drawing did not pass the check. Fix every failure in the check report, then upload the corrected drawing as the next revision of this submission.',
    upload: 'Upload a corrected drawing',
  },
  checkWording: { conversionLabel: 'Conversion' },
  // Plan P4-4b: the placement tool, offered beside this checklist when a tenant turns it on.
  placement,
} as const
